import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2,
  Circle,
  Sunrise,
  Globe,
  MessageSquare,
  ChevronDown,
  ChevronLeft,
  Search,
} from 'lucide-react';
import { getAgendaToday, markAgendaItem, getSpecialRatesCount } from '../../api/agendaChecklist';
import { setTenantId } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import type { AgendaChecklistItem } from '../../types/AgendaChecklist';
import { LopButton } from '../../components/LopButton';
import { t } from '../../i18n/t';
import './AgendaDailyPage.css';
import { showErrorToast, describeError } from '../../components/ErrorToast';

// Inline Israel-flag glyph. (The 🇮🇱 emoji renders as the letters "IL" on Windows,
// so we draw it — consistent across platforms.)
function IsraelFlag({ title }: { title: string }) {
  return (
    <svg width="18" height="13" viewBox="0 0 24 17" role="img" aria-label={title}>
      <title>{title}</title>
      <rect width="24" height="17" fill="#fff" stroke="#d6d6d6" strokeWidth="0.5" />
      <rect y="2.2" width="24" height="2.4" fill="#0038b8" />
      <rect y="12.4" width="24" height="2.4" fill="#0038b8" />
      <g stroke="#0038b8" strokeWidth="0.9" fill="none">
        <path d="M12 5.0 L14.6 9.5 L9.4 9.5 Z" />
        <path d="M12 12.0 L9.4 7.5 L14.6 7.5 Z" />
      </g>
    </svg>
  );
}

type Filter = 'all' | 'undone' | 'blocking' | 'beforeOpen';

interface Group {
  categoryName: string;
  items: AgendaChecklistItem[];
  total: number;
  done: number;
}

export function AgendaDailyPage() {
  const { user } = useAuth();
  const tenantId = user?.currentTenant.tenantId ?? null;
  const [items, setItems] = useState<AgendaChecklistItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [rowError, setRowError] = useState<Record<number, string | null>>({});
  const [busy, setBusy] = useState<number | null>(null);
  const [filter, setFilter] = useState<Filter>('undone');
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [commentOpen, setCommentOpen] = useState<Set<number>>(new Set());
  const [srModal, setSrModal] = useState<{
    item: AgendaChecklistItem;
    count: number | null;
  } | null>(null);

  const load = useCallback(async () => {
    if (tenantId !== null) setTenantId(tenantId);
    setLoading(true);
    setError(null);
    try {
      const data = await getAgendaToday();
      setItems(data);
      setDrafts((prev) => {
        const next: Record<number, string> = {};
        data.forEach((i) => {
          next[i.recId] = prev[i.recId] ?? i.comment ?? '';
        });
        return next;
      });
    } catch (e) {
      showErrorToast(e, { title: t('errorToast.titleLoad') });
      setError(describeError(e).body);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = useCallback(
    async (item: AgendaChecklistItem, nextDone: boolean) => {
      // Special-rates actions: marking DONE opens a confirmation modal that counts
      // the special rates in the portfolio and requires explicit approval.
      if (nextDone && item.actionCode?.startsWith('SPECIAL_RATES')) {
        setSrModal({ item, count: null });
        try {
          const { count } = await getSpecialRatesCount();
          setSrModal({ item, count });
        } catch {
          setSrModal({ item, count: 0 });
        }
        return;
      }
      const comment = (drafts[item.recId] ?? '').trim();
      if (nextDone && item.ask4Comment && !comment) {
        setCommentOpen((p) => new Set(p).add(item.recId));
        setRowError((p) => ({ ...p, [item.recId]: t('agenda.commentRequired') }));
        return;
      }
      setBusy(item.recId);
      setRowError((p) => ({ ...p, [item.recId]: null }));
      try {
        await markAgendaItem({
          recId: item.recId,
          isDone: nextDone,
          comment: comment || null,
          manuallyApproved: false,
        });
        await load();
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setRowError((p) => ({
          ...p,
          [item.recId]: msg.includes('comment_required')
            ? t('agenda.commentRequired')
            : msg,
        }));
      } finally {
        setBusy(null);
      }
    },
    [drafts, load],
  );

  // Approve a special-rates action: marks done with an auto comment recording the count.
  const confirmSr = useCallback(async () => {
    const m = srModal;
    if (!m) return;
    setSrModal(null);
    const comment =
      m.count && m.count > 0
        ? t('agenda.srConfirmedN', { count: String(m.count) })
        : t('agenda.srConfirmedNone');
    setBusy(m.item.recId);
    setRowError((p) => ({ ...p, [m.item.recId]: null }));
    try {
      await markAgendaItem({ recId: m.item.recId, isDone: true, comment, manuallyApproved: true });
      await load();
    } catch (e) {
      setRowError((p) => ({ ...p, [m.item.recId]: e instanceof Error ? e.message : String(e) }));
    } finally {
      setBusy(null);
    }
  }, [srModal, load]);

  const all = useMemo(() => items ?? [], [items]);
  const israeliPending = all.filter((i) => i.required4ValuationIsraeli && !i.isDone).length;
  const foreignPending = all.filter((i) => i.required4ValuationForeign && !i.isDone).length;
  const doneCount = all.filter((i) => i.isDone).length;

  // Filtered + searched, then grouped by category. Each group keeps the FULL
  // today-progress (done/total) so the header reflects real progress, not the
  // filtered subset.
  const groups = useMemo<Group[]>(() => {
    const q = search.trim();
    const matchSearch = (i: AgendaChecklistItem) =>
      !q ||
      i.actionName.includes(q) ||
      (i.actionDescription ?? '').includes(q) ||
      i.categoryName.includes(q);
    const matchFilter = (i: AgendaChecklistItem) => {
      switch (filter) {
        case 'undone':
          return !i.isDone;
        case 'blocking':
          return (i.required4ValuationIsraeli || i.required4ValuationForeign) && !i.isDone;
        case 'beforeOpen':
          return i.beforeOpenDay;
        default:
          return true;
      }
    };

    const order: string[] = [];
    const byCat = new Map<string, AgendaChecklistItem[]>();
    const totals = new Map<string, { total: number; done: number }>();
    all.forEach((i) => {
      const tot = totals.get(i.categoryName) ?? { total: 0, done: 0 };
      tot.total += 1;
      if (i.isDone) tot.done += 1;
      totals.set(i.categoryName, tot);
      if (matchSearch(i) && matchFilter(i)) {
        if (!byCat.has(i.categoryName)) {
          byCat.set(i.categoryName, []);
          order.push(i.categoryName);
        }
        byCat.get(i.categoryName)!.push(i);
      }
    });
    return order.map((c) => ({
      categoryName: c,
      items: byCat.get(c)!,
      total: totals.get(c)!.total,
      done: totals.get(c)!.done,
    }));
  }, [all, filter, search]);

  const toggleCollapse = (cat: string) =>
    setCollapsed((p) => {
      const n = new Set(p);
      if (n.has(cat)) n.delete(cat);
      else n.add(cat);
      return n;
    });

  const toggleComment = (recId: number) =>
    setCommentOpen((p) => {
      const n = new Set(p);
      if (n.has(recId)) n.delete(recId);
      else n.add(recId);
      return n;
    });

  if (loading && !items) {
    return <div className="agenda-daily__muted">{t('common.loading')}</div>;
  }
  if (error) {
    return (
      <div className="agenda-daily">
        <div className="agenda-daily__alert">{t('agenda.errorPrefix', { message: error })}</div>
      </div>
    );
  }

  const filters: { key: Filter; label: string }[] = [
    { key: 'all', label: t('agenda.filterAll') },
    { key: 'undone', label: t('agenda.filterUndone') },
    { key: 'blocking', label: t('agenda.filterBlocking') },
    { key: 'beforeOpen', label: t('agenda.filterBeforeOpen') },
  ];

  return (
    <div className="agenda-daily">
      <div className="agenda-daily__bar">
        <div className="agenda-daily__bar-top">
          <h1 className="agenda-daily__title">{t('agenda.pageTitle')}</h1>
          <span className="agenda-daily__count">
            {t('agenda.progress', { done: String(doneCount), total: String(all.length) })}
          </span>
          <div className="agenda-daily__search">
            <Search size={15} className="agenda-daily__search-icon" />
            <input
              type="text"
              value={search}
              placeholder={t('agenda.searchPlaceholder')}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="agenda-daily__bar-bottom">
          <div className="agenda-daily__chips">
            {filters.map((f) => (
              <button
                key={f.key}
                type="button"
                className={`agenda-daily__filter ${filter === f.key ? 'is-active' : ''}`}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="agenda-daily__gates">
            <span className={`agenda-daily__gate agenda-daily__gate--${israeliPending === 0 ? 'ok' : 'block'}`}>
              <IsraelFlag title={t('agenda.reqIsraeli')} />
              {israeliPending === 0 ? t('agenda.ready') : t('agenda.blockedN', { count: String(israeliPending) })}
            </span>
            <span className={`agenda-daily__gate agenda-daily__gate--${foreignPending === 0 ? 'ok' : 'block'}`}>
              <Globe size={15} />
              {foreignPending === 0 ? t('agenda.ready') : t('agenda.blockedN', { count: String(foreignPending) })}
            </span>
          </div>
        </div>
      </div>

      <div className="agenda-daily__scroll">
        {all.length === 0 && (
          <div className="agenda-daily__muted">{t('agenda.noActionsToday')}</div>
        )}
        {all.length > 0 && groups.length === 0 && (
          <div className="agenda-daily__muted">{t('agenda.noMatch')}</div>
        )}

        {groups.map((g) => {
          const isCollapsed = collapsed.has(g.categoryName);
          return (
            <section key={g.categoryName} className="agenda-daily__group">
              <button
                type="button"
                className="agenda-daily__group-head"
                onClick={() => toggleCollapse(g.categoryName)}
              >
                {isCollapsed ? <ChevronLeft size={16} /> : <ChevronDown size={16} />}
                <span className="agenda-daily__group-name">{g.categoryName}</span>
                <span className={`agenda-daily__group-progress ${g.done === g.total ? 'is-complete' : ''}`}>
                  {g.done}/{g.total}
                </span>
              </button>

              {!isCollapsed && (
                <ul className="agenda-daily__list">
                  {g.items.map((item) => {
                    const showComment = item.ask4Comment && (commentOpen.has(item.recId) || !!(drafts[item.recId] ?? ''));
                    return (
                      <li key={item.recId} className={`agenda-daily__item ${item.isDone ? 'is-done' : ''}`}>
                        <LopButton
                          variant="link"
                          iconOnly
                          className="agenda-daily__check"
                          aria-label={item.isDone ? t('agenda.markUndone') : t('agenda.markDone')}
                          loading={busy === item.recId}
                          icon={item.isDone ? <CheckCircle2 size={20} /> : <Circle size={20} />}
                          onClick={() => toggle(item, !item.isDone)}
                        />

                        <div className="agenda-daily__body">
                          <div className="agenda-daily__name-row">
                            <span className="agenda-daily__name">{item.actionName}</span>
                            {item.required4ValuationIsraeli && <IsraelFlag title={t('agenda.reqIsraeli')} />}
                            {item.required4ValuationForeign && (
                              <span className="agenda-daily__globe" title={t('agenda.reqForeign')}>
                                <Globe size={15} />
                              </span>
                            )}
                            {item.beforeOpenDay && (
                              <span className="agenda-daily__chip agenda-daily__chip--open" title={t('agenda.beforeOpenDay')}>
                                <Sunrise size={12} /> {t('agenda.beforeOpenDay')}
                              </span>
                            )}
                            {item.scope === 'central' && (
                              <span className="agenda-daily__chip agenda-daily__chip--central">{t('agenda.central')}</span>
                            )}
                            {item.ask4Comment && (
                              <button
                                type="button"
                                className={`agenda-daily__comment-btn ${(drafts[item.recId] ?? '') ? 'has-comment' : ''}`}
                                title={t('agenda.commentToggle')}
                                onClick={() => toggleComment(item.recId)}
                              >
                                <MessageSquare size={14} />
                              </button>
                            )}
                          </div>
                          {item.actionDescription && (
                            <div className="agenda-daily__desc">{item.actionDescription}</div>
                          )}
                          {showComment && (
                            <input
                              type="text"
                              className="agenda-daily__comment"
                              placeholder={t('agenda.commentPlaceholder')}
                              value={drafts[item.recId] ?? ''}
                              onChange={(e) => setDrafts((p) => ({ ...p, [item.recId]: e.target.value }))}
                            />
                          )}
                          {rowError[item.recId] && (
                            <div className="agenda-daily__row-error">{rowError[item.recId]}</div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          );
        })}
      </div>

      {srModal && (
        <div
          className="agenda-daily__sr-backdrop"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setSrModal(null);
          }}
        >
          <div className="agenda-daily__sr" role="dialog" aria-modal="true">
            <h3 className="agenda-daily__sr-title">{srModal.item.actionName}</h3>
            {srModal.count === null ? (
              <div className="agenda-daily__muted">{t('common.loading')}</div>
            ) : (
              <>
                <p className={`agenda-daily__sr-count ${srModal.count > 0 ? 'warn' : 'ok'}`}>
                  {srModal.count > 0
                    ? t('agenda.srHasN', { count: String(srModal.count) })
                    : t('agenda.srNone')}
                </p>
                <p className="agenda-daily__sr-q">{t('agenda.srHandledQuestion')}</p>
                <div className="agenda-daily__sr-actions">
                  <LopButton variant="primary" className="agenda-daily__sr-approve" onClick={() => confirmSr()}>
                    {t('agenda.srApprove')}
                  </LopButton>
                  <LopButton variant="secondary" onClick={() => setSrModal(null)}>
                    {t('common.cancel')}
                  </LopButton>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
